import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  Grid3X3, Plus, Save, Trash2, Loader2, AlertTriangle, 
  Check, Move, Settings, Maximize2, RotateCw, Search,
  Ruler, Leaf, Heart, X, FolderOpen, FileText, Layers, Sun, Snowflake, Download
} from 'lucide-react';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import { todayLocal } from '../../utils/dates';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Color palette for crop categories
const categoryColors = {
  tomatoes: '#ef4444',
  peppers: '#f97316',
  squash: '#eab308',
  beans: '#22c55e',
  corn: '#fbbf24',
  greens: '#10b981',
  roots: '#a855f7',
  alliums: '#ec4899',
  brassicas: '#06b6d4',
  cucumbers: '#84cc16',
  melons: '#f472b6',
  herbs: '#34d399',
  nightshades: '#dc2626',
  grains: '#d97706',
  tubers: '#8b5cf6',
  perennial: '#14b8a6',
  specialty: '#6366f1',
  exotic: '#e11d48',
};

const GardenPlanner = () => {
  const { getAuthHeader } = useAuth();
  const canvasRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [layouts, setLayouts] = useState([]);
  const [currentLayout, setCurrentLayout] = useState(null);
  const [crops, setCrops] = useState([]);
  const [spacingGuide, setSpacingGuide] = useState([]);
  const [compatibility, setCompatibility] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  
  // Layout settings
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showBlocksDialog, setShowBlocksDialog] = useState(false);
  const [companionBlocks, setCompanionBlocks] = useState([]);
  const [newLayout, setNewLayout] = useState({
    name: '',
    width: 20,
    height: 15,
    unit: 'feet',
    grid_size: 1
  });
  
  // Drag state
  const [draggedCrop, setDraggedCrop] = useState(null);
  const [placedCrops, setPlacedCrops] = useState([]);
  const [selectedPlacedCrop, setSelectedPlacedCrop] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [isMovingCrop, setIsMovingCrop] = useState(false); // Move mode
  const containerRef = useRef(null);
  
  // Canvas settings
  const [scale, setScale] = useState(30); // pixels per grid cell
  const [gridWidth, setGridWidth] = useState(20);
  const [gridHeight, setGridHeight] = useState(15);
  const [unit, setUnit] = useState('feet');

  // Calculate responsive scale based on container width
  useEffect(() => {
    const calculateScale = () => {
      if (!containerRef.current || !gridWidth) return;
      const containerWidth = containerRef.current.offsetWidth - 32; // Account for padding
      const maxScale = 30;
      const minScale = 15;
      const idealScale = Math.floor(containerWidth / gridWidth);
      setScale(Math.max(minScale, Math.min(maxScale, idealScale)));
    };

    calculateScale();
    window.addEventListener('resize', calculateScale);
    return () => window.removeEventListener('resize', calculateScale);
  }, [gridWidth, currentLayout]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [layoutsRes, cropsRes, spacingRes, categoriesRes] = await Promise.all([
        axios.get(`${API_URL}/api/garden/layouts`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/planner/crops?limit=1000`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/garden/crop-spacing`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/planner/crops/categories`, { headers: getAuthHeader() })
      ]);
      
      const fetchedLayouts = layoutsRes.data.layouts || [];
      setLayouts(fetchedLayouts);
      setCrops(cropsRes.data.crops || []);
      setSpacingGuide(spacingRes.data.spacing_guide || []);
      setCategories(categoriesRes.data.categories || []);
      
      // Auto-load the default garden (or first one if no default set)
      if (fetchedLayouts.length > 0 && !currentLayout) {
        const defaultLayout = fetchedLayouts.find(l => l.is_default) || fetchedLayouts[0];
        await loadLayout(defaultLayout.id);
      } else if (fetchedLayouts.length === 0) {
        // No layouts exist - automatically open the create dialog
        setShowNewDialog(true);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load garden planner data');
    } finally {
      setLoading(false);
    }
  };

  // Load a specific layout
  const loadLayout = async (layoutId) => {
    try {
      const res = await axios.get(`${API_URL}/api/garden/layouts/${layoutId}`, { headers: getAuthHeader() });
      const layout = res.data;
      
      // Check if this layout is marked as default in our layouts list
      const layoutFromList = layouts.find(l => l.id === layoutId);
      const isDefault = layoutFromList?.is_default || layout.is_default || false;
      
      setCurrentLayout({ ...layout, is_default: isDefault });
      setGridWidth(layout.width);
      setGridHeight(layout.height);
      setUnit(layout.unit);
      setPlacedCrops(layout.placed_crops || []);
      setSelectedPlacedCrop(null);
      
      // Validate compatibility
      if (layout.placed_crops?.length > 0) {
        validatePlacement(layout.placed_crops);
      }
    } catch (error) {
      console.error('Failed to load layout:', error);
      toast.error('Failed to load layout');
    }
  };

  // Set a layout as default
  const setAsDefault = async (layoutId) => {
    try {
      await axios.put(`${API_URL}/api/garden/layouts/${layoutId}/default`, {}, { headers: getAuthHeader() });
      // Update local state
      setLayouts(layouts.map(l => ({ ...l, is_default: l.id === layoutId })));
      // Update currentLayout if it's the one being set as default
      if (currentLayout && currentLayout.id === layoutId) {
        setCurrentLayout({ ...currentLayout, is_default: true });
      }
      toast.success('Default garden updated');
    } catch (error) {
      console.error('Failed to set default:', error);
      toast.error('Failed to set default garden');
    }
  };

  const fetchCompanionBlocks = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/garden/companion-blocks`, { headers: getAuthHeader() });
      setCompanionBlocks(res.data.blocks || []);
    } catch (error) {
      console.error('Failed to fetch companion blocks:', error);
    }
  };

  useEffect(() => {
    fetchData();
    fetchCompanionBlocks();
  }, []);

  // Place a companion block on the canvas
  const handlePlaceBlock = (block) => {
    if (!currentLayout) {
      toast.error('Create or load a layout first');
      return;
    }

    // Check if block fits in the garden
    if (block.total_width_ft > gridWidth || block.total_height_ft > gridHeight) {
      toast.error(`Block (${block.total_width_ft}' × ${block.total_height_ft}') is larger than your garden (${gridWidth}' × ${gridHeight}')`);
      return;
    }

    // Set as selected block for placement
    setSelectedBlock(block);
    setShowBlocksDialog(false);
    toast.info(`Tap on the garden to place "${block.name}" block`, { duration: 3000 });
  };

  // Place the selected block at a position
  const placeBlockAtPosition = (x, y) => {
    if (!selectedBlock) return;

    // Check bounds
    if (x < 0 || y < 0 || x + selectedBlock.total_width_ft > gridWidth || y + selectedBlock.total_height_ft > gridHeight) {
      toast.error('Block placement out of bounds');
      return;
    }

    // Add all plants from the block with offset position
    const newCrops = selectedBlock.plants.map(plant => ({
      crop_id: plant.crop_id,
      crop_name: plant.crop_name,
      x: x + plant.x,
      y: y + plant.y,
      width: plant.width,
      height: plant.height,
      spacing_inches: [Math.round(plant.width * 12), Math.round(plant.height * 12)],
      rotation: 0,
      category: 'companion_block',
      block_id: selectedBlock.id
    }));

    setPlacedCrops([...placedCrops, ...newCrops]);
    setSelectedBlock(null);
    toast.success(`Placed "${selectedBlock.name}" (${newCrops.length} plants)`);
    validatePlacement([...placedCrops, ...newCrops]);
  };

  // Filter crops for palette
  const filteredCrops = crops.filter(crop => {
    const matchesSearch = !searchQuery || 
      crop.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || 
      crop.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Get spacing info for a crop - returns actual spacing in feet from spacing_inches
  const getSpacing = (cropId) => {
    // First try to get from spacing guide (has converted values)
    const guideSpacing = spacingGuide.find(s => s.crop_id === cropId);
    if (guideSpacing) {
      return guideSpacing;
    }
    
    // Fallback to crop data directly
    const crop = crops.find(c => c.id === cropId);
    if (crop?.spacing_inches) {
      const [plantIn, rowIn] = crop.spacing_inches;
      return {
        crop_id: cropId,
        spacing_inches: crop.spacing_inches,
        plant_spacing_ft: plantIn / 12,
        row_spacing_ft: rowIn / 12,
        grid_cells: Math.max(1, Math.ceil(Math.max(plantIn, rowIn) / 12))
      };
    }
    
    // Default fallback
    return {
      plant_spacing_ft: 1,
      row_spacing_ft: 2,
      spacing_inches: [12, 24],
      grid_cells: 1
    };
  };

  // Calculate to-scale dimensions for a crop based on spacing_inches
  const calculateCropDimensions = (crop) => {
    const spacing = getSpacing(crop.id);
    // For grid display, we use the larger of plant or row spacing as the main footprint
    // This represents the "zone" that plant needs
    const widthFt = spacing.plant_spacing_ft;
    const heightFt = spacing.row_spacing_ft;
    
    // Minimum 0.25 feet (3 inches) for visibility, max based on actual spacing
    // Round to nearest 0.25 for clean grid alignment
    const roundToQuarter = (val) => Math.max(0.25, Math.round(val * 4) / 4);
    
    return {
      width: roundToQuarter(widthFt),
      height: roundToQuarter(heightFt),
      spacing_inches: spacing.spacing_inches || [12, 24]
    };
  };

  // Create new layout
  const handleCreateLayout = async () => {
    if (!newLayout.name.trim()) {
      toast.error('Please enter a layout name');
      return;
    }
    
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/api/garden/layouts`, {
        ...newLayout,
        placed_crops: []
      }, { headers: getAuthHeader() });
      
      // If this is the first layout, set it as default
      const isFirstLayout = layouts.length === 0;
      const newLayoutData = { ...res.data, is_default: isFirstLayout };
      
      setCurrentLayout(newLayoutData);
      setGridWidth(newLayout.width);
      setGridHeight(newLayout.height);
      setUnit(newLayout.unit);
      setPlacedCrops([]);
      setShowNewDialog(false);
      toast.success('Garden layout created!');
      
      // If first layout, set it as default
      if (isFirstLayout && res.data.id) {
        try {
          await axios.put(`${API_URL}/api/garden/layouts/${res.data.id}/default`, {}, { headers: getAuthHeader() });
        } catch (e) {
          console.log('Could not set default:', e);
        }
      }
      
      fetchData();
    } catch (error) {
      toast.error('Failed to create layout');
    } finally {
      setSaving(false);
    }
  };

  // Save current layout
  const handleSaveLayout = async () => {
    if (!currentLayout) return;
    
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/api/garden/layouts/${currentLayout.id}`, {
        placed_crops: placedCrops
      }, { headers: getAuthHeader() });
      
      setCompatibility(res.data.compatibility);
      toast.success('Layout saved!');
    } catch (error) {
      toast.error('Failed to save layout');
    } finally {
      setSaving(false);
    }
  };

  // Export layout as PDF with plant spacing guide
  const handleExportLayout = async () => {
    if (!canvasRef.current || !currentLayout) {
      toast.error('No layout to export');
      return;
    }
    
    setExporting(true);
    try {
      const canvas = canvasRef.current;
      
      // Create PDF (landscape for garden layouts)
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'letter'
      });
      
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      
      // Title
      pdf.setFontSize(24);
      pdf.setTextColor(22, 101, 52); // Green
      pdf.text(currentLayout.name || 'Garden Layout', pageWidth / 2, 20, { align: 'center' });
      
      // Subtitle with dimensions
      pdf.setFontSize(12);
      pdf.setTextColor(107, 114, 128);
      pdf.text(`${gridWidth} × ${gridHeight} ${unit} | ${placedCrops.length} crops planted`, pageWidth / 2, 28, { align: 'center' });
      
      // Add garden image
      const canvasDataUrl = canvas.toDataURL('image/png', 1.0);
      const imgWidth = Math.min(pageWidth - margin * 2, 180);
      const imgHeight = (canvas.height / canvas.width) * imgWidth;
      const imgX = (pageWidth - imgWidth) / 2;
      pdf.addImage(canvasDataUrl, 'PNG', imgX, 35, imgWidth, Math.min(imgHeight, 100));
      
      // Crop count summary
      const cropCounts = {};
      placedCrops.forEach(crop => {
        cropCounts[crop.crop_name] = (cropCounts[crop.crop_name] || 0) + 1;
      });
      
      // Plant Spacing Guide section
      let yPos = 35 + Math.min(imgHeight, 100) + 15;
      
      pdf.setFontSize(14);
      pdf.setTextColor(22, 101, 52);
      pdf.text('Plant Spacing Guide', margin, yPos);
      yPos += 8;
      
      // Table header
      pdf.setFontSize(9);
      pdf.setTextColor(55, 65, 81);
      pdf.setFont(undefined, 'bold');
      pdf.text('Crop', margin, yPos);
      pdf.text('Qty', margin + 50, yPos);
      pdf.text('Size', margin + 65, yPos);
      pdf.text('Position', margin + 90, yPos);
      pdf.setFont(undefined, 'normal');
      yPos += 5;
      
      // Draw line under header
      pdf.setDrawColor(200, 200, 200);
      pdf.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 4;
      
      // Crop details table
      const uniqueCrops = [...new Set(placedCrops.map(c => c.crop_name))];
      uniqueCrops.forEach((cropName, idx) => {
        if (yPos > pageHeight - 20) {
          pdf.addPage();
          yPos = 20;
        }
        
        const cropsOfType = placedCrops.filter(c => c.crop_name === cropName);
        const firstCrop = cropsOfType[0];
        const positions = cropsOfType.map(c => `(${Math.round(c.x)},${Math.round(c.y)})`).join(', ');
        
        pdf.setFontSize(8);
        pdf.setTextColor(55, 65, 81);
        pdf.text(cropName, margin, yPos);
        pdf.text(`${cropsOfType.length}`, margin + 50, yPos);
        pdf.text(`${firstCrop.width}×${firstCrop.height} ${unit}`, margin + 65, yPos);
        
        // Truncate positions if too long
        const maxPosWidth = pageWidth - margin - 95;
        let posText = positions;
        if (pdf.getTextWidth(positions) > maxPosWidth) {
          posText = positions.substring(0, 50) + '...';
        }
        pdf.text(posText, margin + 90, yPos);
        
        yPos += 5;
      });
      
      // Summary statistics
      yPos += 5;
      if (yPos > pageHeight - 30) {
        pdf.addPage();
        yPos = 20;
      }
      
      pdf.setDrawColor(200, 200, 200);
      pdf.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 8;
      
      pdf.setFontSize(10);
      pdf.setTextColor(22, 101, 52);
      pdf.text('Summary', margin, yPos);
      yPos += 6;
      
      pdf.setFontSize(9);
      pdf.setTextColor(55, 65, 81);
      pdf.text(`Total Crops: ${placedCrops.length}`, margin, yPos);
      pdf.text(`Varieties: ${uniqueCrops.length}`, margin + 40, yPos);
      pdf.text(`Garden Size: ${gridWidth * gridHeight} sq ${unit}`, margin + 80, yPos);
      
      // Footer with date
      pdf.setFontSize(8);
      pdf.setTextColor(156, 163, 175);
      pdf.text(`Generated by Fencepost | ${new Date().toLocaleDateString()}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
      
      // Save PDF
      const filename = `${(currentLayout.name || 'garden-layout').replace(/\s+/g, '-').toLowerCase()}-${todayLocal()}.pdf`;
      pdf.save(filename);
      
      toast.success('Layout exported as PDF!');
      
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export layout');
    } finally {
      setExporting(false);
    }
  };

  // Delete layout
  const handleDeleteLayout = async (layoutId) => {
    try {
      await axios.delete(`${API_URL}/api/garden/layouts/${layoutId}`, {
        headers: getAuthHeader()
      });
      
      if (currentLayout?.id === layoutId) {
        setCurrentLayout(null);
        setPlacedCrops([]);
      }
      toast.success('Layout deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete layout');
    }
  };

  // Handle drag start from palette
  const handleDragStart = (crop, e) => {
    const dimensions = calculateCropDimensions(crop);
    setDraggedCrop({
      ...crop,
      width: dimensions.width,
      height: dimensions.height,
      spacing_inches: dimensions.spacing_inches
    });
    setIsDragging(true);
  };

  // Handle tap to select crop on mobile
  const handleCropTap = (crop) => {
    if (!currentLayout) return;
    const dimensions = calculateCropDimensions(crop);
    setDraggedCrop({
      ...crop,
      width: dimensions.width,
      height: dimensions.height,
      spacing_inches: dimensions.spacing_inches
    });
    setIsDragging(true);
    toast.info(`Tap on the garden to place ${crop.name}`, { duration: 2000 });
  };

  // Handle drop on canvas
  const handleCanvasDrop = (e) => {
    if (!draggedCrop || !currentLayout) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    // Allow sub-foot precision - snap to 0.25 ft increments for realistic planning
    const rawX = (e.clientX - rect.left) / scale;
    const rawY = (e.clientY - rect.top) / scale;
    const x = Math.round(rawX * 4) / 4; // Snap to 0.25 ft
    const y = Math.round(rawY * 4) / 4;
    
    placeCropAtPosition(x, y);
  };

  // Handle touch/tap on canvas for mobile
  const handleCanvasTouch = (e) => {
    if ((!draggedCrop && !selectedBlock) || !currentLayout) return;
    
    e.preventDefault();
    const touch = e.touches[0] || e.changedTouches[0];
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const rawX = (touch.clientX - rect.left) / scale;
    const rawY = (touch.clientY - rect.top) / scale;
    const x = Math.round(rawX * 4) / 4;
    const y = Math.round(rawY * 4) / 4;
    
    if (selectedBlock) {
      placeBlockAtPosition(x, y);
    } else {
      placeCropAtPosition(x, y);
    }
  };

  // Handle touch end on canvas for move mode
  const handleCanvasTouchMove = (e) => {
    if (!isMovingCrop || selectedPlacedCrop === null || !currentLayout) return;
    
    e.preventDefault();
    const touch = e.touches[0] || e.changedTouches[0];
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const rawX = (touch.clientX - rect.left) / scale;
    const rawY = (touch.clientY - rect.top) / scale;
    const x = Math.round(rawX * 4) / 4;
    const y = Math.round(rawY * 4) / 4;
    
    moveCropToPosition(x, y);
  };

  // Common place crop logic
  const placeCropAtPosition = (x, y) => {
    if (!draggedCrop) return;
    
    // Check bounds
    if (x < 0 || y < 0 || x + draggedCrop.width > gridWidth || y + draggedCrop.height > gridHeight) {
      toast.error('Crop placement out of bounds');
      setDraggedCrop(null);
      setIsDragging(false);
      return;
    }
    
    // Add to placed crops with to-scale spacing data
    const newCrop = {
      crop_id: draggedCrop.id,
      crop_name: draggedCrop.name,
      x,
      y,
      width: draggedCrop.width,
      height: draggedCrop.height,
      spacing_inches: draggedCrop.spacing_inches,
      rotation: 0,
      category: draggedCrop.category
    };
    
    setPlacedCrops([...placedCrops, newCrop]);
    setDraggedCrop(null);
    setIsDragging(false);
    
    // Check compatibility
    validatePlacement([...placedCrops, newCrop]);
    toast.success(`Placed ${newCrop.crop_name}`);
  };

  // Move placed crop
  const handleMovePlacedCrop = (index, newX, newY) => {
    const crop = placedCrops[index];
    
    // Check bounds
    if (newX < 0 || newY < 0 || newX + crop.width > gridWidth || newY + crop.height > gridHeight) {
      toast.error('Cannot move - would go out of bounds');
      return false;
    }
    
    const updated = [...placedCrops];
    updated[index] = { ...updated[index], x: newX, y: newY };
    setPlacedCrops(updated);
    validatePlacement(updated);
    return true;
  };

  // Start move mode for selected crop
  const startMoveCrop = () => {
    if (selectedPlacedCrop === null) return;
    setIsMovingCrop(true);
    toast.info('Tap on the garden to move the crop', { duration: 2000 });
  };

  // Execute move to new position
  const moveCropToPosition = (newX, newY) => {
    if (selectedPlacedCrop === null) return;
    
    const success = handleMovePlacedCrop(selectedPlacedCrop, newX, newY);
    if (success) {
      toast.success('Crop moved');
    }
    setIsMovingCrop(false);
  };

  // Rotate placed crop 90 degrees
  const handleRotateCrop = (index) => {
    const updated = [...placedCrops];
    const crop = updated[index];
    
    // Swap width and height
    const newWidth = crop.height;
    const newHeight = crop.width;
    
    // Check if rotated crop would be out of bounds
    if (crop.x + newWidth > gridWidth || crop.y + newHeight > gridHeight) {
      toast.error('Cannot rotate - would go out of bounds');
      return;
    }
    
    // Update crop with swapped dimensions and increment rotation
    updated[index] = {
      ...crop,
      width: newWidth,
      height: newHeight,
      rotation: (crop.rotation + 90) % 360
    };
    
    setPlacedCrops(updated);
    toast.success('Rotated 90°');
  };

  // Remove placed crop
  const handleRemoveCrop = (index) => {
    const updated = placedCrops.filter((_, i) => i !== index);
    setPlacedCrops(updated);
    setSelectedPlacedCrop(null);
    validatePlacement(updated);
  };

  // Validate placement compatibility
  const validatePlacement = async (crops) => {
    if (crops.length < 2) {
      setCompatibility(null);
      return;
    }
    
    try {
      const res = await axios.post(`${API_URL}/api/planner/check-compatibility`, {
        crop_ids: crops.map(c => c.crop_id)
      }, { headers: getAuthHeader() });
      
      setCompatibility(res.data);
    } catch (error) {
      console.error('Compatibility check failed:', error);
    }
  };

  // Render canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const width = gridWidth * scale;
    const height = gridHeight * scale;
    
    canvas.width = width;
    canvas.height = height;
    
    // Clear canvas
    ctx.fillStyle = '#f5f0e8';
    ctx.fillRect(0, 0, width, height);
    
    // Draw grid
    ctx.strokeStyle = '#d4c9b8';
    ctx.lineWidth = 1;
    
    for (let x = 0; x <= gridWidth; x++) {
      ctx.beginPath();
      ctx.moveTo(x * scale, 0);
      ctx.lineTo(x * scale, height);
      ctx.stroke();
    }
    
    for (let y = 0; y <= gridHeight; y++) {
      ctx.beginPath();
      ctx.moveTo(0, y * scale);
      ctx.lineTo(width, y * scale);
      ctx.stroke();
    }
    
    // Draw scale markers
    ctx.fillStyle = '#8b7355';
    ctx.font = '10px sans-serif';
    for (let x = 0; x <= gridWidth; x += 5) {
      ctx.fillText(`${x}${unit === 'feet' ? 'ft' : 'm'}`, x * scale + 2, 12);
    }
    for (let y = 5; y <= gridHeight; y += 5) {
      ctx.fillText(`${y}`, 2, y * scale - 2);
    }
    
    // Draw placed crops with to-scale rendering
    // First, calculate adjacency relationships for companion highlighting
    const adjacencyMap = new Map(); // Map crop index to {companions: [], conflicts: []}
    
    // Check each pair of crops for adjacency and compatibility
    placedCrops.forEach((crop1, i) => {
      if (!adjacencyMap.has(i)) adjacencyMap.set(i, { companions: [], conflicts: [] });
      
      placedCrops.forEach((crop2, j) => {
        if (i >= j) return; // Avoid checking same pair twice
        
        // Calculate distance between crop centers
        const dx = (crop1.x + crop1.width / 2) - (crop2.x + crop2.width / 2);
        const dy = (crop1.y + crop1.height / 2) - (crop2.y + crop2.height / 2);
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        // Consider crops adjacent if within 4 feet of each other
        const adjacencyThreshold = 4;
        if (distance <= adjacencyThreshold) {
          // Check compatibility based on category
          const cat1 = crop1.category || crop1.crop_name?.toLowerCase().split(' ')[0];
          const cat2 = crop2.category || crop2.crop_name?.toLowerCase().split(' ')[0];
          
          // Check if they're in the compatibility issues
          const hasConflict = compatibility?.issues?.some(issue => 
            (issue.crop1_id === crop1.crop_id && issue.crop2_id === crop2.crop_id) ||
            (issue.crop1_id === crop2.crop_id && issue.crop2_id === crop1.crop_id)
          );
          
          // Check if they're good companions
          const isCompanion = compatibility?.good_combinations?.some(combo =>
            (combo.crop1_id === crop1.crop_id && combo.crop2_id === crop2.crop_id) ||
            (combo.crop1_id === crop2.crop_id && combo.crop2_id === crop1.crop_id)
          );
          
          if (!adjacencyMap.has(j)) adjacencyMap.set(j, { companions: [], conflicts: [] });
          
          if (hasConflict) {
            adjacencyMap.get(i).conflicts.push(j);
            adjacencyMap.get(j).conflicts.push(i);
          } else if (isCompanion) {
            adjacencyMap.get(i).companions.push(j);
            adjacencyMap.get(j).companions.push(i);
          }
        }
      });
    });
    
    // Now draw each crop with appropriate highlighting
    placedCrops.forEach((crop, index) => {
      const color = categoryColors[crop.category] || '#6b7280';
      const isSelected = selectedPlacedCrop === index;
      const cropX = crop.x * scale;
      const cropY = crop.y * scale;
      const cropW = crop.width * scale;
      const cropH = crop.height * scale;
      
      const adjacency = adjacencyMap.get(index) || { companions: [], conflicts: [] };
      const hasConflict = adjacency.conflicts.length > 0;
      const hasCompanion = adjacency.companions.length > 0;
      
      // Draw glow/highlight for companion status
      if (hasConflict) {
        // Red glow for conflicts
        ctx.shadowColor = '#ef4444';
        ctx.shadowBlur = 12;
        ctx.fillStyle = '#ef444440';
        ctx.fillRect(cropX - 2, cropY - 2, cropW + 4, cropH + 4);
        ctx.shadowBlur = 0;
      } else if (hasCompanion) {
        // Green glow for good companions
        ctx.shadowColor = '#22c55e';
        ctx.shadowBlur = 10;
        ctx.fillStyle = '#22c55e30';
        ctx.fillRect(cropX - 2, cropY - 2, cropW + 4, cropH + 4);
        ctx.shadowBlur = 0;
      }
      
      // Fill with semi-transparent color
      ctx.fillStyle = color + '40';
      ctx.fillRect(cropX, cropY, cropW, cropH);
      
      // Draw inner "plant" circle to represent actual plant
      const plantRadius = Math.min(cropW, cropH) * 0.3;
      const centerX = cropX + cropW / 2;
      const centerY = cropY + cropH / 2;
      ctx.beginPath();
      ctx.arc(centerX, centerY, plantRadius, 0, Math.PI * 2);
      ctx.fillStyle = color + '80';
      ctx.fill();
      
      // Border - color based on companion status
      let borderColor = color;
      let borderWidth = 2;
      
      if (isSelected) {
        borderColor = '#000';
        borderWidth = 3;
      } else if (hasConflict) {
        borderColor = '#ef4444';
        borderWidth = 3;
      } else if (hasCompanion) {
        borderColor = '#22c55e';
        borderWidth = 3;
      }
      
      ctx.strokeStyle = borderColor;
      ctx.lineWidth = borderWidth;
      ctx.strokeRect(cropX, cropY, cropW, cropH);
      
      // Draw small indicator icons in corner
      if (hasConflict) {
        // Red warning triangle in corner
        ctx.fillStyle = '#ef4444';
        ctx.beginPath();
        ctx.moveTo(cropX + cropW - 12, cropY + 2);
        ctx.lineTo(cropX + cropW - 2, cropY + 2);
        ctx.lineTo(cropX + cropW - 7, cropY + 10);
        ctx.closePath();
        ctx.fill();
      } else if (hasCompanion) {
        // Green check circle in corner
        ctx.fillStyle = '#22c55e';
        ctx.beginPath();
        ctx.arc(cropX + cropW - 7, cropY + 7, 5, 0, Math.PI * 2);
        ctx.fill();
      }
      
      // Label - only show if enough space
      ctx.fillStyle = '#1f2937';
      if (cropW > 30 && cropH > 20) {
        ctx.font = 'bold 10px sans-serif';
        const label = crop.crop_name.length > 10 ? crop.crop_name.slice(0, 8) + '..' : crop.crop_name;
        ctx.fillText(label, cropX + 3, cropY + 11);
      }
      
      // Show dimensions on selected crop
      if (isSelected && crop.spacing_inches) {
        ctx.font = '9px sans-serif';
        ctx.fillStyle = '#374151';
        const dimText = `${crop.spacing_inches[0]}"×${crop.spacing_inches[1]}"`;
        ctx.fillText(dimText, cropX + 2, cropY + cropH - 3);
      }
    });
    
    // Draw connection lines between conflicting crops
    if (compatibility?.issues) {
      compatibility.issues.forEach(issue => {
        const crop1Idx = placedCrops.findIndex(c => c.crop_id === issue.crop1_id);
        const crop2Idx = placedCrops.findIndex(c => c.crop_id === issue.crop2_id);
        const crop1 = placedCrops[crop1Idx];
        const crop2 = placedCrops[crop2Idx];
        
        if (crop1 && crop2) {
          ctx.strokeStyle = '#ef4444';
          ctx.lineWidth = 2;
          ctx.setLineDash([5, 3]);
          ctx.beginPath();
          ctx.moveTo((crop1.x + crop1.width / 2) * scale, (crop1.y + crop1.height / 2) * scale);
          ctx.lineTo((crop2.x + crop2.width / 2) * scale, (crop2.y + crop2.height / 2) * scale);
          ctx.stroke();
          ctx.setLineDash([]);
        }
      });
    }
    
    // Draw connection lines between companion crops (subtle green)
    if (compatibility?.good_combinations) {
      compatibility.good_combinations.forEach(combo => {
        const crop1Idx = placedCrops.findIndex(c => c.crop_id === combo.crop1_id);
        const crop2Idx = placedCrops.findIndex(c => c.crop_id === combo.crop2_id);
        const crop1 = placedCrops[crop1Idx];
        const crop2 = placedCrops[crop2Idx];
        
        if (crop1 && crop2) {
          // Only draw if crops are adjacent
          const dx = (crop1.x + crop1.width / 2) - (crop2.x + crop2.width / 2);
          const dy = (crop1.y + crop1.height / 2) - (crop2.y + crop2.height / 2);
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance <= 4) {
            ctx.strokeStyle = '#22c55e50';
            ctx.lineWidth = 2;
            ctx.setLineDash([3, 3]);
            ctx.beginPath();
            ctx.moveTo((crop1.x + crop1.width / 2) * scale, (crop1.y + crop1.height / 2) * scale);
            ctx.lineTo((crop2.x + crop2.width / 2) * scale, (crop2.y + crop2.height / 2) * scale);
            ctx.stroke();
            ctx.setLineDash([]);
          }
        }
      });
    }
    
  }, [placedCrops, gridWidth, gridHeight, scale, selectedPlacedCrop, compatibility, unit]);

  // Handle canvas click
  const handleCanvasClick = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) / scale;
    const y = (e.clientY - rect.top) / scale;
    const snapX = Math.round(x * 4) / 4;
    const snapY = Math.round(y * 4) / 4;
    
    // If in move mode, move the selected crop
    if (isMovingCrop && selectedPlacedCrop !== null && currentLayout) {
      moveCropToPosition(snapX, snapY);
      return;
    }
    
    // If we have a selected block to place
    if (selectedBlock && currentLayout) {
      placeBlockAtPosition(snapX, snapY);
      return;
    }
    
    // If we have a selected crop to place, place it
    if (draggedCrop && currentLayout) {
      placeCropAtPosition(snapX, snapY);
      return;
    }
    
    // Otherwise, find clicked crop to select
    const clickedIndex = placedCrops.findIndex(crop => 
      x >= crop.x && x <= crop.x + crop.width &&
      y >= crop.y && y <= crop.y + crop.height
    );
    
    setSelectedPlacedCrop(clickedIndex >= 0 ? clickedIndex : null);
    setIsMovingCrop(false); // Reset move mode when selecting new crop
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="garden-planner-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in w-full overflow-hidden" data-testid="garden-planner">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:gap-4">
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-xl sm:text-2xl md:text-3xl font-bold text-foreground">
            Garden Layout Planner
          </h1>
          <p className="text-muted-foreground mt-1 text-xs sm:text-sm truncate">
            {currentLayout ? (
              <span>
                <span className="text-primary font-medium">{currentLayout.name}</span> 
                {' '}({gridWidth}×{gridHeight} {unit})
              </span>
            ) : (
              'Select or create a garden layout'
            )}
          </p>
        </div>
        <div className="flex gap-1.5 sm:gap-2 flex-wrap items-center">
          {/* Garden Layout Selector Dropdown */}
          <Select 
            value={currentLayout?.id || ''} 
            onValueChange={(value) => {
              if (value === 'new') {
                setShowNewDialog(true);
              } else if (value) {
                loadLayout(value);
              }
            }}
          >
            <SelectTrigger 
              className="w-[140px] sm:w-[200px] text-xs sm:text-sm" 
              data-testid="garden-layout-selector"
            >
              <div className="flex items-center gap-1.5 sm:gap-2 truncate">
                <FolderOpen className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                <SelectValue placeholder="Select garden..." />
              </div>
            </SelectTrigger>
            <SelectContent>
              {layouts.map((layout) => (
                <SelectItem key={layout.id} value={layout.id} data-testid={`layout-option-${layout.id}`}>
                  <div className="flex items-center gap-2">
                    {layout.is_default && (
                      <span className="text-xs text-green-600">★</span>
                    )}
                    <span className="truncate">{layout.name}</span>
                    <span className="text-xs text-muted-foreground ml-1">
                      ({layout.width}×{layout.height})
                    </span>
                  </div>
                </SelectItem>
              ))}
              <SelectItem value="new" className="border-t mt-1 pt-1" data-testid="create-new-layout-option">
                <div className="flex items-center gap-2 text-primary">
                  <Plus className="w-4 h-4" />
                  <span>Create New Layout</span>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
          
          {/* Set as Default Button */}
          {currentLayout && !currentLayout.is_default && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setAsDefault(currentLayout.id)}
              className="text-[10px] sm:text-xs text-muted-foreground hover:text-primary px-1.5 sm:px-2 h-8"
              title="Set as default garden"
              data-testid="set-default-btn"
            >
              <span className="hidden sm:inline">★ Default</span>
              <span className="sm:hidden">★</span>
            </Button>
          )}
          
          {/* Delete Current Layout */}
          {currentLayout && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => {
                if (window.confirm(`Delete "${currentLayout.name}"? This cannot be undone.`)) {
                  handleDeleteLayout(currentLayout.id);
                }
              }}
              className="text-destructive hover:text-destructive hover:bg-destructive/10 h-8 w-8 p-0"
              title="Delete this layout"
              data-testid="delete-current-layout-btn"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
          
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowBlocksDialog(true)}
            disabled={!currentLayout}
            className={`h-8 px-2 sm:px-3 text-xs ${currentLayout ? 'border-green-500/50 text-green-700 hover:bg-green-50' : ''}`}
            data-testid="companion-blocks-btn"
          >
            <Layers className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
            <span className="hidden sm:inline">Blocks</span>
          </Button>
          {currentLayout && (
            <>
              <Button onClick={handleSaveLayout} disabled={saving} size="sm" className="h-8 px-2 sm:px-3 text-xs" data-testid="save-layout-btn">
                {saving ? <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 animate-spin sm:mr-1" /> : <Save className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />}
                <span className="hidden sm:inline">Save</span>
              </Button>
              <Button 
                onClick={handleExportLayout} 
                disabled={exporting || placedCrops.length === 0} 
                variant="outline"
                size="sm"
                className="text-emerald-700 border-emerald-500/50 hover:bg-emerald-50 h-8 px-2 sm:px-3 text-xs"
                data-testid="export-layout-btn"
              >
                {exporting ? <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 animate-spin sm:mr-1" /> : <FileText className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />}
                <span className="hidden sm:inline">PDF</span>
              </Button>
            </>
          )}
        </div>
      </div>

      {/* New Layout Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogTrigger asChild>
          <span className="hidden" />
        </DialogTrigger>
            <DialogContent aria-describedby="new-layout-description">
              <DialogHeader>
                <DialogTitle className="font-heading">Create Garden Layout</DialogTitle>
              </DialogHeader>
              <p id="new-layout-description" className="sr-only">Set up dimensions for your new garden layout</p>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Layout Name</Label>
                  <Input
                    value={newLayout.name}
                    onChange={(e) => setNewLayout({ ...newLayout, name: e.target.value })}
                    placeholder="e.g., Main Vegetable Garden"
                    data-testid="layout-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Width</Label>
                    <Input
                      type="number"
                      min="1"
                      max="200"
                      value={newLayout.width}
                      onChange={(e) => setNewLayout({ ...newLayout, width: parseFloat(e.target.value) || 1 })}
                      data-testid="width-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Height</Label>
                    <Input
                      type="number"
                      min="1"
                      max="200"
                      value={newLayout.height}
                      onChange={(e) => setNewLayout({ ...newLayout, height: parseFloat(e.target.value) || 1 })}
                      data-testid="height-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Unit</Label>
                  <Select
                    value={newLayout.unit}
                    onValueChange={(v) => setNewLayout({ ...newLayout, unit: v })}
                  >
                    <SelectTrigger data-testid="unit-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="feet">Feet</SelectItem>
                      <SelectItem value="meters">Meters</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Grid Size ({newLayout.unit})</Label>
                  <Select
                    value={newLayout.grid_size.toString()}
                    onValueChange={(v) => setNewLayout({ ...newLayout, grid_size: parseFloat(v) })}
                  >
                    <SelectTrigger data-testid="grid-size-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0.5">0.5 (Fine)</SelectItem>
                      <SelectItem value="1">1 (Standard)</SelectItem>
                      <SelectItem value="2">2 (Coarse)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  onClick={handleCreateLayout} 
                  disabled={saving}
                  className="w-full"
                  data-testid="create-layout-submit"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  Create Layout
                </Button>
              </div>
            </DialogContent>
          </Dialog>

      {/* Companion Blocks Dialog */}
      <Dialog open={showBlocksDialog} onOpenChange={setShowBlocksDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Layers className="w-5 h-5 text-green-600" />
              Companion Planting Blocks
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Pre-made sets of companion plants. Each block contains plants known to grow well together, already sized and arranged.
          </p>
          
          <div className="flex-1 overflow-y-auto space-y-3 mt-4 pr-2">
            {companionBlocks.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : (
              companionBlocks.map((block) => (
                <div 
                  key={block.id}
                  className="p-4 rounded-lg border hover:border-green-500/50 hover:bg-green-50/50 transition-colors"
                  data-testid={`block-${block.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-heading font-medium text-foreground">{block.name}</h4>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          block.season === 'warm' ? 'bg-orange-100 text-orange-700' :
                          block.season === 'cool' ? 'bg-blue-100 text-blue-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {block.season === 'warm' && <Sun className="w-3 h-3 inline mr-1" />}
                          {block.season === 'cool' && <Snowflake className="w-3 h-3 inline mr-1" />}
                          {block.season}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{block.description}</p>
                      <div className="flex flex-wrap gap-1 mb-2">
                        {block.plants.slice(0, 5).map((plant, idx) => (
                          <span 
                            key={idx} 
                            className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground"
                          >
                            {plant.crop_name.split(' - ')[0]}
                          </span>
                        ))}
                        {block.plants.length > 5 && (
                          <span className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground">
                            +{block.plants.length - 5} more
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Size: {block.total_width_ft}&apos; × {block.total_height_ft}&apos; • {block.plants.length} plants
                      </p>
                    </div>
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 flex-shrink-0"
                      onClick={() => handlePlaceBlock(block)}
                      disabled={!currentLayout || block.total_width_ft > gridWidth || block.total_height_ft > gridHeight}
                      data-testid={`place-block-${block.id}`}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Place
                    </Button>
                  </div>
                  {currentLayout && (block.total_width_ft > gridWidth || block.total_height_ft > gridHeight) && (
                    <p className="text-xs text-destructive mt-2">
                      Block too large for your {gridWidth}&apos; × {gridHeight}&apos; garden
                    </p>
                  )}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Main Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6 max-w-full overflow-hidden">
        {/* Crop Palette */}
        <div className="lg:col-span-1 space-y-4 min-w-0 w-full">
          <Card className="card-farm max-w-full overflow-hidden w-full">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-base sm:text-lg flex items-center gap-2">
                <Leaf className="w-5 h-5 text-primary" />
                Crop Palette
              </CardTitle>
              {currentLayout && (
                <p className="text-xs text-muted-foreground mt-1">
                  Tap a crop, then tap the garden
                </p>
              )}
            </CardHeader>
            <CardContent>
              {/* Search and Filter */}
              <div className="space-y-3 mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search crops..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="palette-search"
                  />
                </div>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger data-testid="palette-category-filter">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name} ({cat.count})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Crop List */}
              <div className="max-h-[400px] overflow-y-auto space-y-2 px-2">
                {filteredCrops.map((crop) => {
                  const spacing = getSpacing(crop.id);
                  const dimensions = calculateCropDimensions(crop);
                  const color = categoryColors[crop.category] || '#6b7280';
                  const spacingInches = crop.spacing_inches || spacing.spacing_inches || [12, 24];
                  const isSelected = draggedCrop?.id === crop.id;
                  
                  return (
                    <div
                      key={crop.id}
                      draggable={!!currentLayout}
                      onDragStart={(e) => handleDragStart(crop, e)}
                      onClick={() => handleCropTap(crop)}
                      onTouchEnd={(e) => { e.preventDefault(); handleCropTap(crop); }}
                      className={`p-3 rounded-lg border transition-colors ${
                        !currentLayout 
                          ? 'opacity-50 cursor-not-allowed' 
                          : isSelected
                            ? 'border-primary ring-2 ring-primary'
                            : 'cursor-pointer hover:border-primary/50 active:ring-1 active:ring-primary'
                      }`}
                      style={{
                        backgroundColor: isSelected 
                          ? `${color}20` 
                          : `${color}08`
                      }}
                      data-testid={`palette-crop-${crop.id}`}
                    >
                      <div className="flex flex-col items-center text-center min-w-0 w-full">
                        <p className="text-sm font-medium truncate w-full">{crop.name}</p>
                        <p className="text-xs text-muted-foreground truncate w-full">
                          {spacingInches[0]}&quot; × {spacingInches[1]}&quot; ({dimensions.width.toFixed(1)}&apos; × {dimensions.height.toFixed(1)}&apos;)
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Legend */}
          <Card className="card-farm max-w-full overflow-hidden">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-sm flex items-center gap-2">
                <Ruler className="w-4 h-4" />
                Scale Guide
              </CardTitle>
            </CardHeader>
            <CardContent className="text-xs text-muted-foreground space-y-2">
              <p><strong>Grid:</strong> 1 cell = 1 {unit === 'feet' ? 'foot' : 'meter'}</p>
              <p><strong>To-Scale:</strong> Crops show actual footprint</p>
              <div className="flex items-center gap-2 mt-2">
                <div className="w-4 h-1 bg-red-500 rounded"></div>
                <span>Red = Incompatible</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Canvas Area */}
        <div className="lg:col-span-3 space-y-4 min-w-0 w-full">
          {/* Selected crop indicator */}
          {draggedCrop && (
            <div className="flex items-center gap-2 p-3 bg-primary/10 border border-primary/30 rounded-lg">
              <Check className="w-4 h-4 text-primary" />
              <span className="text-sm text-primary font-medium">
                Tap on the garden to place: {draggedCrop.name}
              </span>
              <Button 
                variant="ghost" 
                size="sm" 
                className="ml-auto h-7 px-2"
                onClick={() => { setDraggedCrop(null); setIsDragging(false); }}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}

          {/* Selected block indicator */}
          {selectedBlock && (
            <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
              <Layers className="w-4 h-4 text-green-600" />
              <span className="text-sm text-green-700 font-medium">
                Tap on the garden to place: &quot;{selectedBlock.name}&quot; ({selectedBlock.plants.length} plants, {selectedBlock.total_width_ft}&apos; × {selectedBlock.total_height_ft}&apos;)
              </span>
              <Button 
                variant="ghost" 
                size="sm" 
                className="ml-auto h-7 px-2"
                onClick={() => setSelectedBlock(null)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}

          {/* Canvas */}
          <Card className="card-farm max-w-full overflow-hidden">
            <CardContent className="p-2 sm:p-4" ref={containerRef}>
              {currentLayout ? (
                <div 
                  className="overflow-auto bg-muted/30 rounded-lg"
                  style={{ maxHeight: 'calc(100vh - 350px)', minHeight: '250px' }}
                  data-testid="garden-canvas-container"
                >
                  <div className="p-2 sm:p-4 flex justify-center">
                    <canvas
                      ref={canvasRef}
                      onDrop={handleCanvasDrop}
                      onDragOver={(e) => e.preventDefault()}
                      onClick={handleCanvasClick}
                      onTouchEnd={isMovingCrop ? handleCanvasTouchMove : handleCanvasTouch}
                      className={`border-2 border-dashed rounded block max-w-full ${
                        isMovingCrop ? 'border-blue-500 cursor-move' :
                        draggedCrop || selectedBlock ? 'border-primary cursor-pointer' : 
                        'border-primary/30 cursor-crosshair'
                      }`}
                      data-testid="garden-canvas"
                    />
                  </div>
                </div>
              ) : (
                <div className="h-[400px] flex flex-col items-center justify-center text-muted-foreground">
                  <Grid3X3 className="w-16 h-16 mb-4 opacity-30" />
                  <p className="text-lg font-medium">No Layout Selected</p>
                  <p className="text-sm">Create a new layout or load an existing one to start planning</p>
                  <Button 
                    className="mt-4" 
                    onClick={() => setShowNewDialog(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create New Layout
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Companion Planting Legend */}
          {currentLayout && placedCrops.length > 1 && (
            <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground px-2">
              <span className="font-medium">Legend:</span>
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 rounded border-2 border-green-500 bg-green-500/20"></div>
                <span>Good companions</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 rounded border-2 border-red-500 bg-red-500/20"></div>
                <span>Avoid planting together</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 rounded border-2 border-gray-400 bg-gray-400/20"></div>
                <span>Neutral</span>
              </div>
            </div>
          )}

          {/* Selected Crop Actions */}
          {selectedPlacedCrop !== null && placedCrops[selectedPlacedCrop] && (
            <Card className="card-farm border-primary">
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <h4 className="font-medium truncate">{placedCrops[selectedPlacedCrop].crop_name}</h4>
                    <p className="text-sm text-muted-foreground">
                      Position: ({placedCrops[selectedPlacedCrop].x.toFixed(1)}, {placedCrops[selectedPlacedCrop].y.toFixed(1)}) {unit} • 
                      Size: {placedCrops[selectedPlacedCrop].width.toFixed(1)}&apos; × {placedCrops[selectedPlacedCrop].height.toFixed(1)}&apos;
                      {placedCrops[selectedPlacedCrop].rotation > 0 && (
                        <span className="ml-1 text-primary">
                          (rotated {placedCrops[selectedPlacedCrop].rotation}°)
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button
                      variant={isMovingCrop ? "default" : "outline"}
                      size="sm"
                      onClick={startMoveCrop}
                      title="Move crop"
                      className={isMovingCrop ? "bg-blue-600 hover:bg-blue-700" : ""}
                      data-testid="move-crop-btn"
                    >
                      <Move className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRotateCrop(selectedPlacedCrop)}
                      title="Rotate 90°"
                      data-testid="rotate-crop-btn"
                    >
                      <RotateCw className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRemoveCrop(selectedPlacedCrop)}
                      title="Delete crop"
                      data-testid="remove-crop-btn"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                {isMovingCrop && (
                  <p className="text-sm text-blue-600 mt-2">
                    👆 Tap on the garden to move this crop to a new location
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Compatibility Status */}
          {compatibility && (
            <Card className={`card-farm ${compatibility.is_compatible ? 'border-green-500/50' : 'border-red-500/50'}`}>
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Heart className={`w-5 h-5 ${compatibility.is_compatible ? 'text-green-500' : 'text-red-500'}`} />
                  Companion Planting Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                {compatibility.is_compatible ? (
                  <div className="flex items-center gap-2 text-green-600">
                    <Check className="w-5 h-5" />
                    <span>All crops are compatible! Great choices.</span>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-red-600">
                      <AlertTriangle className="w-5 h-5" />
                      <span>{compatibility.issues.length} potential conflict(s) found</span>
                    </div>
                    <div className="space-y-2">
                      {compatibility.issues.map((issue, idx) => (
                        <div key={idx} className="text-sm p-2 rounded bg-red-500/10 text-red-700 dark:text-red-400">
                          {issue.message}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {compatibility.good_combinations?.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm font-medium text-green-600 mb-2">Good Combinations:</p>
                    <div className="flex flex-wrap gap-2">
                      {compatibility.good_combinations.map((combo, idx) => (
                        <Badge key={idx} className="bg-green-500/10 text-green-700 dark:text-green-400">
                          {combo.crop1} + {combo.crop2}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {compatibility.suggestions?.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm font-medium text-muted-foreground mb-2">Suggested additions:</p>
                    <div className="flex flex-wrap gap-2">
                      {compatibility.suggestions.map((sug, idx) => (
                        <Badge key={idx} variant="outline" className="cursor-pointer hover:bg-primary/10">
                          <Plus className="w-3 h-3 mr-1" />
                          {sug.crop_name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Stats */}
          {currentLayout && placedCrops.length > 0 && (
            <div className="grid grid-cols-3 gap-1.5 sm:gap-4">
              <Card className="card-farm overflow-hidden">
                <CardContent className="p-2 sm:p-4 text-center">
                  <p className="text-lg sm:text-2xl font-bold text-primary">{placedCrops.length}</p>
                  <p className="text-[10px] sm:text-sm text-muted-foreground">Crops</p>
                </CardContent>
              </Card>
              <Card className="card-farm overflow-hidden">
                <CardContent className="p-2 sm:p-4 text-center">
                  <p className="text-lg sm:text-2xl font-bold text-primary">
                    {placedCrops.reduce((sum, c) => sum + c.width * c.height, 0).toFixed(1)}
                  </p>
                  <p className="text-[10px] sm:text-sm text-muted-foreground">sq {unit}</p>
                </CardContent>
              </Card>
              <Card className="card-farm overflow-hidden">
                <CardContent className="p-2 sm:p-4 text-center">
                  <p className="text-lg sm:text-2xl font-bold text-primary">
                    {Math.round((placedCrops.reduce((sum, c) => sum + c.width * c.height, 0) / (gridWidth * gridHeight)) * 100)}%
                  </p>
                  <p className="text-[10px] sm:text-sm text-muted-foreground">Used</p>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GardenPlanner;
